import { Injectable } from '@angular/core';
import { FeatureFlagsService } from '../feature-flags/feature-flags.service';
import { WorkspaceModuleGroup } from '../../models/workspace-module-group.model';
import {
  FEATURE_FLAG_ABC_ANALYSIS,
  FEATURE_FLAG_ACCESS_CONTROL,
  FEATURE_FLAG_AGENT_LIBRARY,
  FEATURE_FLAG_CONSTRAINED_SERVICE_LEVEL_OPTIMIZATION,
  FEATURE_FLAG_DEMAND_FORECASTING,
  FEATURE_FLAG_DEMAND_PLANNING,
  FEATURE_FLAG_DEMAND_PREDICTION,
  FEATURE_FLAG_DEO_AGENT,
  FEATURE_FLAG_HOME,
  FEATURE_FLAG_INVENTORY_OPTIMIZATION,
  FEATURE_FLAG_LEAD_TIME_PREDICTION,
  FEATURE_FLAG_OPTIMAL_REPLENISHMENT_QUANTITY,
  FEATURE_FLAG_SUPPLY_VARIABILITY,
  FEATURE_FLAG_TOOLS
} from '../feature-flags/feature-flags.constants';

export const HOME_URL = '/home';
export const START_URL = '/start';
export const DEO_AI_ASSIST_URL = '/deo-ai-assist';

// Same groups as the side menu, which hides a whole group when its group flag is off, whatever its modules say.
// Settings and the rail extras are left out on purpose: they are not a place to work in,
// so having them must not keep a DEO-only user on an empty home page.
const WORKSPACE_MODULE_GROUPS: WorkspaceModuleGroup[] = [
  {
    groupFlag: FEATURE_FLAG_DEMAND_PLANNING,
    moduleFlags: [FEATURE_FLAG_DEMAND_FORECASTING, FEATURE_FLAG_DEMAND_PREDICTION]
  },
  {
    groupFlag: FEATURE_FLAG_INVENTORY_OPTIMIZATION,
    moduleFlags: [
      FEATURE_FLAG_ABC_ANALYSIS,
      FEATURE_FLAG_CONSTRAINED_SERVICE_LEVEL_OPTIMIZATION,
      FEATURE_FLAG_LEAD_TIME_PREDICTION,
      FEATURE_FLAG_OPTIMAL_REPLENISHMENT_QUANTITY,
      FEATURE_FLAG_SUPPLY_VARIABILITY
    ]
  },
  { moduleFlags: [FEATURE_FLAG_AGENT_LIBRARY, FEATURE_FLAG_ACCESS_CONTROL, FEATURE_FLAG_TOOLS] }
];

/** Reads flags already loaded: callers must await `ensureFeatureFlagsLoaded()` first. */
@Injectable({ providedIn: 'root' })
export class LandingRouteService {
  constructor(private readonly featureFlagsService: FeatureFlagsService) {}

  resolveLandingUrl(): string {
    if (this.isEntitledToDeoOnly()) {
      return DEO_AI_ASSIST_URL;
    }
    return this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_HOME) ? HOME_URL : START_URL;
  }

  isEntitledToDeoOnly(): boolean {
    return this.featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT) && !this.isEntitledToAnyWorkspaceModule();
  }

  private isEntitledToAnyWorkspaceModule(): boolean {
    return WORKSPACE_MODULE_GROUPS.some(group => this.isEntitledToAnyModuleIn(group));
  }

  private isEntitledToAnyModuleIn(group: WorkspaceModuleGroup): boolean {
    const isGroupEnabled = !group.groupFlag || this.featureFlagsService.isFeatureEnabled(group.groupFlag);
    return isGroupEnabled && group.moduleFlags.some(flag => this.featureFlagsService.isFeatureEnabled(flag));
  }
}
